import { useState, useEffect } from 'react';
import {
  Box, Button, Chip, CircularProgress, TextField, MenuItem, Typography, Divider,
} from '@mui/material';
import { Badge as ErpIcon, CheckCircle as DoneIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Puente ATS → ERP (PLAN_ERP.md §11.1): sección del dialog de contratación
 * que crea la ficha de colaborador ERP prellenada desde la application.
 * Botón EXPLÍCITO (no hook automático) y visible solo si la organización
 * tiene el ERP habilitado y el usuario es super-admin (restricción vigente).
 */
const AtsErpRecordSection = ({ application }) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert, erpEnabled, erpRole, user } = useStore.getState();

  const visible = erpEnabled && (user?.rol === 'super-admin' || Boolean(erpRole));

  const [expanded, setExpanded] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [quotationId, setQuotationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdId, setCreatedId] = useState(application?.erpEmployeeRecordId || null);

  useEffect(() => {
    setCreatedId(application?.erpEmployeeRecordId || null);
    setExpanded(false);
    setQuotationId('');
  }, [application?.uid, application?.erpEmployeeRecordId]);

  useEffect(() => {
    const loadQuotations = async () => {
      if (!expanded || quotations.length > 0) return;
      try {
        setLoading(true);
        const response = await api.get('/erp/quotations', {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        // Sin subcotizaciones: la sub se asigna al pasar a contrato (ERP)
        const list = (response.data.response?.data || []).filter((q) =>
          ['quoted', 'in_use'].includes(q.status) && !q.parentQuotationId
        );
        setQuotations(list);
      } catch (err) {
        console.error(err);
        showAlert(t('erp.atsBridge.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    loadQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleCreate = async () => {
    if (!quotationId) return;
    try {
      setCreating(true);
      const response = await api.post(
        '/erp/employee-records/from-application',
        {
          organizationId: organization,
          applicationId: application.uid,
          quotationId,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.data.response?.status_response === 'OK') {
        setCreatedId(response.data.response.data?.uid || 'done');
        showAlert(t('erp.atsBridge.created'), 'success');
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.errorMessage || '';
      showAlert(
        message.includes('already has') ? t('erp.atsBridge.alreadyLinked') : t('erp.atsBridge.error'),
        'error'
      );
      if (message.includes('already has')) setCreatedId('done');
    } finally {
      setCreating(false);
    }
  };

  if (!visible || !application) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      {createdId ? (
        <Chip icon={<DoneIcon />} color="success" variant="outlined" label={t('erp.atsBridge.done')} />
      ) : !expanded ? (
        <Button size="small" variant="outlined" startIcon={<ErpIcon />} onClick={() => setExpanded(true)}
          sx={{ textTransform: 'none' }}>
          {t('erp.atsBridge.button')}
        </Button>
      ) : (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('erp.atsBridge.pickQuotation')}</Typography>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField select size="small" sx={{ minWidth: 320 }}
                label={t('erp.quotations.title')} value={quotationId}
                onChange={(e) => setQuotationId(e.target.value)}>
                {quotations.map((q) => (
                  <MenuItem key={q.uid} value={q.uid}>
                    {q.quotationNumber ? `${q.quotationNumber} · ` : ''}{q.clientName} — {q.jobTitleName} ({q.period?.startDate})
                  </MenuItem>
                ))}
              </TextField>
              <Button size="small" variant="contained" disabled={!quotationId || creating} onClick={handleCreate}
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
                {creating ? t('erp.common.saving') : t('erp.atsBridge.create')}
              </Button>
            </Box>
          )}
          {expanded && !loading && quotations.length === 0 && (
            <Typography variant="caption" color="text.secondary">{t('erp.atsBridge.noQuotations')}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AtsErpRecordSection;
